import React, { ReactElement, ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { Link } from '../../Devon/Components/InkyLink';
import { Strip } from '../../Models/Article';
import { DiscoverItem } from '../../Models/DiscoverItem';
import { Thumbnail } from '../../Models/Thumbnail';
import ProductPoster from '../KodanshaUIKit/ProductPoster/ProductPoster';
import SeriesPoster from '../KodanshaUIKit/SeriesPoster/SeriesPoster';
import KodanshaUnderlinedText from '../KodanshaUIKit/KodanshaUnderlinedText/KodanshaUnderlinedText';
import KodanshaArrow from '../KodanshaUIKit/KodanshaArrow/KodanshaArrow';
import KodanshaSlider from '../KodanshaSlider';
import { readerReadableUrlOrDefault } from '../../utils/utils';
import './ChapterWheelStrip.scss';

const dateFormatter = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });

interface ChapterWheelItemProps {
    item: DiscoverItem;
}

function ChapterWheelItem({ item }: ChapterWheelItemProps): ReactElement {
    const history = useHistory();

    let linkTo: string;
    switch (item.presentationMode) {
        case 'Product':
            linkTo = `/products/${item.content?.volumeReadableUrl ?? item.content?.volumeId ?? ''}`;
            break;
        default:
            linkTo = `/series/${item.content?.seriesReadableUrl ?? item.content?.seriesId ?? ''}`;
            break;
    }

    let thumbnail: Thumbnail | undefined;
    if (item.content?.thumbnails) {
        thumbnail = item.content.thumbnails[0];
    }

    let poster: ReactNode;
    switch (item.presentationMode) {
        case 'Product':
            poster = (
                <ProductPoster
                    preloadImage={false}
                    ratio={100}
                    src={thumbnail?.url}
                    hover={!!linkTo}
                    isOnSale={item.content?.isOnSale}
                    alt={`product cover for ${item.content?.chapterName} from kodansha`}
                />
            );
            break;
        default:
            poster = (
                <SeriesPoster
                    preloadImage={false}
                    ratio={100}
                    src={thumbnail?.url ?? ''}
                    color={thumbnail?.color}
                    isGray={thumbnail?.color ==="grayscale"}
                    s={{ width: 240, aspectRatio: 1.25 }}
                    m={{ width: 162, aspectRatio: 1.25 }}
                    l={{ width: 212, aspectRatio: 1.25 }}
                    xl={{ width: 338, aspectRatio: 1.25 }}
                    alt={`series cover for ${item.content?.chapterName} from kodansha`}
                />
            );
            break;
    }

    let title: string;
    switch (item.presentationMode) {
        case 'Product':
            title = item.content?.volumeName ?? '';
            break;
        default:
            title = item.content?.seriesName ?? '';
            break;
    }

    return (
        <div className='ChapterWheelItem'>
            <Link className='ChapterWheelItem-productSeriesLink' to={linkTo}>
                <div className='ChapterWheelItem-thumbnail'>{poster}</div>
                <div className='ChapterWheelItem-title'>{title}</div>
                <div className='ChapterWheelItem-subTitle'>{item.content?.relativeName}</div>
                <div className='ChapterWheelItem-publishDate'>
                    {dateFormatter.format(new Date(item.content?.publishDate ?? ''))}
                </div>
            </Link>
            <Link
                className='ChapterWheelItem-readerLink'
                to={{
                    pathname: readerReadableUrlOrDefault(
                        item.content
                    ),
                    state: { returnPath: history.location.pathname },
                }}
            >
                <KodanshaUnderlinedText text='READ CHAPTER' />
            </Link>
        </div>
    );
}

interface ChapterWheelStripProps {
    strip: Strip;
}

export function ChapterWheelStrip({ strip: { params } }: ChapterWheelStripProps): ReactElement {
    // @todo define type
    const { title, subTitle, callToActionText, callToActionUrl, items } = params as {
        title: string;
        subTitle: string;
        callToActionText?: string;
        callToActionUrl?: string;
        items: DiscoverItem[];
    };
    return (
        <div className='ChapterWheelStrip'>
            <KodanshaSlider title={title} subtitle={subTitle} thumbnailSelector='.ChapterWheelItem-thumbnail'>
                {items.slice(0, 20).map(item => <ChapterWheelItem key={item.content?.id} item={item} />)}
            </KodanshaSlider>
            {!!callToActionText && !!callToActionUrl && (
                <Link to={callToActionUrl}>
                    <KodanshaArrow text={callToActionText} withArrow={true} />
                </Link>
            )}
        </div>
    );
}
